import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Assessment } from 'src/app/models/assessment';
import { UnionDomain } from 'src/app/services/union.domain';
import { AssessmentService } from 'src/app/services/assessment.service';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { SubdomainService } from 'src/app/services/subdomain.service';
import * as XLSX from 'xlsx';
import { environment } from 'src/environments/environment';
import { WardService } from 'src/app/services/ward.service';

@Component({
  selector: 'app-assessment-page',
  templateUrl: './assessment-page.component.html',
  styleUrls: ['./assessment-page.component.scss'],
})
export class AssessmentPageComponent implements OnInit {
  data: Assessment[] = []; // Your data array
  paginatedData: any[] = [];
  currentPage: number = 1;
  itemsPerPage: number | 'All' = 10;
  totalPages: number = 0;
  totalItems: number = 0; // New property to store total items
  currentSortColumn: string = 'id';
  currentSortDirection: 'asc' | 'desc' = 'asc';
  filters: { [key: string]: string } = {};
  pageSizes: (number | string)[] = [10, 20, 50, 100, 500, 1000, 'All'];
  $subscriptions: Subscription[] = [];
  upCardBanner!: string;
  holdingCardBanner!: string;
  fileName!: string;
  private apiUrl = environment.apiBaseUrl;
  wards: any[] = []; // Add this for wards data
  fullTextSearchTerm: string = ''; // Add this for full text search
  isLoading: boolean = false; // Add loading state

  @ViewChild('holdingNumberFilter') holdingNumberFilter!: ElementRef;
  @ViewChild('nameFilter') nameFilter!: ElementRef;
  @ViewChild('guardianNameFilter') guardianNameFilter!: ElementRef;
  @ViewChild('wardNumberFilter') wardNumberFilter!: ElementRef;
  @ViewChild('mobileNumberFilter') mobileNumberFilter!: ElementRef;
  @ViewChild('khanarDhoronFilter') khanarDhoronFilter!: ElementRef;
  @ViewChild('gramOrRastaFilter') gramOrRastaFilter!: ElementRef;

  constructor(
    private http: HttpClient,
    private router: Router,
    public unionDomain: UnionDomain,
    private assessmentService: AssessmentService,
    private toastr: ToastrService,
    private subdomainService: SubdomainService,
    private wardService: WardService
  ) {}

  ngOnInit(): void {
    // Initialize your data and pagination
    this.upCardBanner =
      '../../../../assets/img/' +
      this.subdomainService.getSubdomain() +
      'UpSmartCard.bmp';
    this.holdingCardBanner =
      '../../../../assets/img/' +
      this.subdomainService.getSubdomain() +
      'HoldingCard.bmp';
    this.fileName = this.subdomainService.getSubdomain() + '_Excel_Sheet.xlsx';
    this.getWards();
    this.loadData();
  }

  // Get the wards data
  private getWards = async (): Promise<void> => {
    this.$subscriptions.push(
      this.wardService.getWardList().subscribe({
        next: (res: any) => {
          this.wards = res;
        },
      })
    );
  };

  // Full text search method
  performFullTextSearch(event: Event): void {
    const searchInput = event.target as HTMLInputElement;
    this.fullTextSearchTerm = searchInput.value;
    
    // Reset to first page when searching
    this.currentPage = 1;
    this.loadData();
  }

  // Filter by ward selection
  filterByWard(event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    const wardNumber = selectElement.value;
    
    if (wardNumber) {
      this.filters['filter[ward_number]'] = wardNumber;
    } else {
      delete this.filters['filter[ward_number]'];
    }
    this.loadData();
  }

  deleteAssesment = (id: any): void => {
    if (confirm('Are you sure you want to delete ?'))
      this.$subscriptions.push(
        this.assessmentService.deleteAssesment(id).subscribe({
          next: (res) => {
            this.toastr.success(res.message);
            this.loadData();
          },
          error: (err) => {
            this.toastr.error(err.message);
          },
        })
      );
  };

  navigateToEditAssesmentPage = (id: any): void => {
    this.assessmentService.sharedAssesment = true;
    this.router.navigate(['admin/assessment-create'], {
      queryParams: { id: id },
    });
  };

  navigateToUserStatus = (qr_code: any): void => {
    this.router.navigate(['admin/tax-collection'], {
      queryParams: { qr_code: qr_code },
    });
  };

  navigateToIdCardPage = (id: any): void => {
    this.assessmentService.sharedAssesment = true;
    this.router.navigate(['admin/up-card-print'], { queryParams: { id: id } });
  };

  navigateToHoldingCardPage = (id: any): void => {
    this.assessmentService.sharedAssesment = true;
    this.router.navigate(['admin/holding-card-print'], {
      queryParams: { id: id },
    });
  };

  navigateToScannedPage = (qr_code: any): void => {
    this.router.navigate(['users-status'], {
      queryParams: { qr_code: qr_code },
    });
  };

  copyQrCode = (qr_code: any): void => {
    navigator.clipboard.writeText(qr_code).then(() => {
      this.toastr.success('QR Code copied to clipboard!');
    }).catch(() => {
      this.toastr.error('Failed to copy QR Code to clipboard');
    });
  };

  loadData(): void {
    this.isLoading = true;
    
    const params: any = {
      sortBy: this.currentSortColumn,
      sortOrder: this.currentSortDirection.toUpperCase(),
      ...this.filters,
    };

    // Add pagination parameters only if not requesting "All"
    if (this.itemsPerPage !== 'All') {
      params.pageNumber = this.currentPage.toString();
      params.pageSize = this.itemsPerPage.toString();
    }

    // Base URL
    let url = `${this.apiUrl}/api/assessment/getfilteredAssessments`;
    
    // Convert params to query string
    const queryParams = new URLSearchParams();
    
    // Add all regular params
    Object.keys(params).forEach(key => {
      queryParams.append(key, params[key]);
    });
    
    // Add search term parameter separately if it exists
    if (this.fullTextSearchTerm && this.fullTextSearchTerm.trim() !== '') {
      queryParams.append('searchTerm', this.fullTextSearchTerm.trim());
      console.log('Sending search term:', this.fullTextSearchTerm.trim());
    }
    
    // Complete URL with query string
    const fullUrl = `${url}?${queryParams.toString()}`;
    console.log('Request URL:', fullUrl);

    // Make the HTTP request
    this.http.get<any>(fullUrl).subscribe({
      next: (response) => {
        this.data = response.data;
        this.totalPages = response.totalPages;
        this.totalItems = response.totalCount;
        this.paginatedData = this.data;
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading data:', error);
        this.isLoading = false;
        this.toastr.error('ডেটা লোড করতে সমস্যা হয়েছে');
      }
    });
  }

  clearFilters(): void {
    this.filters = {};
    this.fullTextSearchTerm = ''; // Clear full text search term
    this.holdingNumberFilter.nativeElement.value = '';
    this.nameFilter.nativeElement.value = '';
    this.guardianNameFilter.nativeElement.value = '';
    this.wardNumberFilter.nativeElement.value = '';
    this.mobileNumberFilter.nativeElement.value = '';
    this.khanarDhoronFilter.nativeElement.value = '';
    this.gramOrRastaFilter.nativeElement.value = '';
    
    // Reset search input
    const searchInput = document.getElementById('fullTextSearch') as HTMLInputElement;
    if (searchInput) {
      searchInput.value = '';
    }
    
    // Reset ward dropdown
    const wardDropdown = document.getElementById('wardDropdown') as HTMLSelectElement;
    if (wardDropdown) {
      wardDropdown.value = '';
    }
    
    this.loadData();
  }

  changePage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadData();
    }
  }

  sortData(column: string): void {
    if (this.currentSortColumn === column) {
      this.currentSortDirection =
        this.currentSortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.currentSortColumn = column;
      this.currentSortDirection = 'asc';
    }
    this.loadData();
  }

  filterData(event: any, column: string): void {
    const filterValue = event.target.value.toLowerCase();
    this.filters[`filter[${column}]`] = filterValue;
    this.loadData();
  }

  changePageSize(event: any): void {
    this.itemsPerPage = event.target.value === 'All' ? 'All' : +event.target.value;
    this.currentPage = 1; // Reset to the first page
    this.loadData();
  }

  getPaginationPages(): number[] {
    const pages: number[] = [];
    const maxPagesToShow = 5;
    const halfPagesToShow = Math.floor(maxPagesToShow / 2);

    let startPage = Math.max(1, this.currentPage - halfPagesToShow);
    let endPage = Math.min(this.totalPages, this.currentPage + halfPagesToShow);

    if (this.currentPage - halfPagesToShow < 1) {
      endPage = Math.min(
        this.totalPages,
        endPage + (halfPagesToShow - (this.currentPage - 1))
      );
    }

    if (this.currentPage + halfPagesToShow > this.totalPages) {
      startPage = Math.max(
        1,
        startPage - (this.currentPage + halfPagesToShow - this.totalPages)
      );
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  }
  exportTableToExcel() {
    let element = document.getElementById('tableId');
    const ws: XLSX.WorkSheet = XLSX.utils.table_to_sheet(element);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, this.fileName);
  }
}
